<template>
  <div
    class="flex justify-content-end align-items-center"
    :class="{
      'pr-3': rightPadding,
      'pb-2': bottomPadding,
    }"
  >
    <slot
      name="frame-footer"
      :submit="submit"
      :delete="deleteEntity"
      :cancel="back"
      :validation="validation"
    >
      <ValidationSummaryIndicator
        v-if="isModifierSet(ModifierType.LISA3)"
        :validation="validation"
        :extra-validation-rules="extraValidationRules"
        class="mr-auto"
      />

      <slot name="warning">
        <div v-if="warning != null" class="flex align-items-center">
          <Message class="mr-2 mb-0" :severity="warning.variant">
            <i v-if="warning.icon" class="mr-1" :class="warning.icon" />
            {{ warning.text }}
          </Message>
        </div>
      </slot>

      <slot
        name="leading"
        :submit="submit"
        :delete="deleteEntity"
        :cancel="back"
        :validation="validation"
      />

      <LISAButton
        v-if="!create && deleteAble"
        id="input-frame-delete-button"
        :data-testid="`${testPrefix}input-frame-delete-button`"
        class="mr-2"
        severity="danger"
        outlined
        :disabled="loading || preventDelete"
        @click="deleteEntity"
      >
        <i class="fas fa-trash-alt mr-1" />{{ deleteLabel ?? t("global.delete") }}
        <InfoTooltip
          v-if="deleteTooltipContent != null"
          :text="deleteTooltipContent"
          :variant="preventDelete ? 'error' : undefined"
        >
          <template #activator>
            <i class="fas fa-info-circle ml-1" />
          </template>
        </InfoTooltip>
      </LISAButton>

      <LISAButton
        v-if="!okOnly"
        data-testid="input-frame-cancel-button"
        class="mr-2"
        severity="secondary"
        @click="back"
      >
        <i class="fas fa-times mr-1" />{{ t("global.cancel") }}
      </LISAButton>

      <slot
        name="leading-submit"
        :submit="submit"
        :delete="deleteEntity"
        :cancel="back"
        :validation="validation"
      />

      <LISAButton
        type="submit"
        class="text-truncate"
        :data-testid="`${testPrefix}input-frame-submit-button`"
        :disabled="submitDisabled"
        :severity="okButtonStrippedVariant"
        @click.prevent.stop="submit"
      >
        <slot name="frame-ok">
          <i class="fas fa-save mr-1" />
          {{ create ? t("global.create") : t("global.saveChanges") }}
        </slot>
      </LISAButton>

      <slot
        name="trailing"
        :submit="submit"
        :delete="deleteEntity"
        :cancel="back"
        :validation="validation"
      />
    </slot>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed } from "vue";
import LISAButton from "@LISAPrime/LISAButton.vue";
import Message from "primevue/message";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import type { FormValidationController } from "@/modules/formValidation";
import ValidationSummaryIndicator from "./ValidationSummaryIndicator.vue";
import type { ExtraValidationRule } from "./ExtraValidationRule";
import isModifierSet from "@/functions/userModifier";
import { ModifierType } from "@/functions/userModifierEnum";

const props = withDefaults(
  defineProps<{
    validation: FormValidationController;
    rightPadding?: boolean;
    bottomPadding?: boolean;
    testPrefix?: string;
    extraValidationRules?: readonly ExtraValidationRule[];
    warning?: { variant?: string; icon?: string; text: string } | null;
    loading?: boolean;
    deleteTooltipContent?: string | null;
    deleteLabel?: string;
    preventDelete?: boolean;
    create?: boolean;
    okButtonVariant?: string;
    okOnly?: boolean;
    deleteAble?: boolean;
    okDisabled?: boolean;
  }>(),
  {
    rightPadding: true,
    bottomPadding: true,
    testPrefix: "",
    extraValidationRules: () => [],
    warning: null,
    loading: false,
    deleteTooltipContent: null,
    deleteLabel: undefined,
    preventDelete: false,
    create: false,
    okButtonVariant: "p-button-success",
    okOnly: false,
    deleteAble: false,
    okDisabled: false,
  },
);

const okButtonStrippedVariant = computed(() => {
  const chunks = props.okButtonVariant.split("-");
  return chunks[chunks.length - 1];
});

const emit = defineEmits<{
  deleteEntity: [];
  submit: [];
  back: [];
}>();

const submitDisabled = computed(
  () =>
    props.loading ||
    props.okDisabled ||
    !props.validation.canSubmit ||
    props.extraValidationRules.some((rule) => !rule.condition),
);

function submit() {
  emit("submit");
}

function deleteEntity() {
  emit("deleteEntity");
}

function back() {
  emit("back");
}

const { t } = useTranslation();
</script>
